import { ReactNode, RefAttributes } from 'react';
import { Link, LinkProps } from 'react-router-dom';

import { TrackingEventProperties } from '../../hooks/trackingEventTaxonomy';
import { useTracking } from '../../hooks/useTracking';
import { Text } from '../Text';

import styles from './breadcrumb.css';

type LinkListItemProps = LinkProps &
  RefAttributes<HTMLAnchorElement> & {
    children: ReactNode;
    trackingProps?: TrackingEventProperties;
  };

type ActiveListItemProps = {
  /** If active, the breadcrumb item is rendered as plain text instead of as a link. */
  active: boolean;
  children: ReactNode;
  trackingProps?: TrackingEventProperties;
};

export type ListItemProps = LinkListItemProps | ActiveListItemProps;

// Discriminates between props for an ActiveListItem and for a LinkListItem:
function isActiveListItem(props: ListItemProps): props is ActiveListItemProps {
  return Object.prototype.hasOwnProperty.call(props, 'active');
}

function ListItem(props: ListItemProps) {
  const { track } = useTracking();

  if (isActiveListItem(props)) {
    const { children } = props;
    return (
      <li>
        <Text className={styles.active}>{children}</Text>
      </li>
    );
  }

  const { children, trackingProps, ...linkProps } = props;
  return (
    <li className={styles.itemWithLink}>
      <Link
        className={styles.link}
        onClick={() => {
          track('Link clicked', {
            'location component': 'Breadcrumb',
            'location component name': 'Breadcrumb Link',
            actor: 'User',
            field: String(children),
            value: linkProps.to.toString(),
            ...trackingProps,
          });
        }}
        {...linkProps}
      >
        {children}
      </Link>
      <span className={styles.separator} aria-hidden>
        &gt;
      </span>
    </li>
  );
}

const BreadcrumbBase = ({ children, testid }: { children: ReactNode; testid?: string }) => (
  <nav data-testid={testid} aria-label="Breadcrumb">
    <ol className={styles.list}>{children}</ol>
  </nav>
);

export const Breadcrumb = Object.assign(BreadcrumbBase, {
  Item: ListItem,
});
